/**
 * modules, libraries, Angular
 */
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import { RouterModule, Routes } from '@angular/router';
import { ShareButtonsModule } from 'ngx-sharebuttons/buttons';
import { GoogleMapsModule } from '@angular/google-maps';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
// for AoT support, https://github.com/ocombe/"@ngx-translate/core"#aot
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ToastrModule } from 'ngx-toastr';
import { RecaptchaModule } from 'ng-recaptcha';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTableModule } from '@angular/material/table';
import { MatListModule } from '@angular/material/list';
import { MatToolbarModule } from '@angular/material/toolbar';
/**
 * guards
 */
import {
  CanActivateViaAuthGuard,
  CanActivateViaSuperAdminGuard,
  CanActivateViaAlreadyLoggedInGuard,
} from './common/auth.guard';

/**
 * components
 */
import { AppComponent } from './app.component';
import { HomeComponent } from './home/home.component';
import { CarouselComponent } from './home/carousel/carousel.component';
import { AboutUsComponent } from './aboutUs/about-us.component';
import { MissionComponent } from './aboutUs/mission/mission.component';
import { HistoryComponent } from './aboutUs/history/history.component';
import { BoardOfDirectorsComponent } from './aboutUs/board-of-directors/board-of-directors.component';
import { ABOUT_US_ROUTE } from './aboutUs/about-us.routes';
import { NewsMediaComponent } from './news-media/news-media.component';
import { NewsMediaContentComponent } from './news-media/news-media-content/news-media-content.component';
import { NEWS_MEDIA_ROUTE } from './news-media/news-media.routes';
import { DonorsServicesComponent } from './donors-services/donors-services.component';
import { WhatWeDoComponent } from './footer/whatWeDo/what-we-do.component';
import { SmartResponseComponent } from './footer/whatWeDo/smartResponse/smart-response.component';
import { ResearchComponent } from './footer/whatWeDo/research/research.component';
import { AdvocacyComponent } from './footer/whatWeDo/advocacy/advocacy.component';
import { IntroComponent } from './intro/intro.component';
import { ResponseProfileComponent } from './searchResults/responseProfile/response-profile.component';
import { OrgBackgroundComponent } from './searchResults/orgBackground/org-background.component';
import { OrgLocationComponent } from './searchResults/orgLocation/org-location.component';
import { OrgDisasterComponent } from './searchResults/orgDisaster/org-disaster.component';
import { DisasterAboutComponent } from './searchResults/disasterAbout/disaster-about.component';
import { DapDonationComponent } from './searchResults/dapDonation/dap-donation.component';
import { GiveLivelyDonationComponent } from './searchResults/dapDonation/givelively-donation.component';
import { DapDonationThanksComponent } from './searchResults/dapDonationThanks/dap-donation-thanks.component';
import { FooterComponent } from './footer/footer.component';
import { VolunteersPageComponent } from './volunteersPage/volunteersPage.component';
import { DisastersPageComponent } from './home/disastersPage/disastersPage.component';
import { PrivacyPolicyPageComponent } from './privacyPage/privacyPage.component';
import { HeaderComponent } from './header/header.component';
import { PickerMenuComponent } from './header/pickerMenu/picker-menu.component';
import { OptionPickerComponent } from './header/optionPicker/option-picker.component';
import { CurrencyPickerComponent } from './header/currencyPicker/currency-picker.component';
import { LanguagePickerComponent } from './header/languagePicker/language-picker.component';
import { MarketingComponent } from './home/marketing/marketing.component';
import { SocialNetworkComponent } from './socialNetwork/socialNetwork.component';
import { SearchComponent } from './home/search/search.component';
import { DisasterListHomeComponent } from './home/disasterList/disasterList.component';
import { ReportModalComponent } from './searchResults/reportModal/report-modal.component';
import { LocationAboutComponent } from './searchResults/locationAbout/location-about.component';
import { UnreadMessagesComponent } from './header/unread-messages/unread-messages.component';
import { LastUpdatedSurveyResultsComponent } from './searchResults/lastUpdatedTimestamp/last-updated.component';
import { ParseVideoUrlComponent } from './searchResults/parseVideoUrl/parse-video-url.component';
import { SurveyResponseComponent } from './searchResults/response/response.component';
import { SendMessageComponent } from './sendMessage/send-message.component';
import { ShortUrlComponent } from './shortUrl/short-url.component';
import { ShareButtonsComponent } from './shareButtons/shareButtons.component';
import { ShareStripeComponent } from './shareStripe/shareStripe.component';
import { DonationModalComponent } from './searchResults/donationHelper/donationModal/donation-modal.component';

/**
 * services
 */
import { AuthenticationService } from './login/login.service';
import { UnreadMessagesService } from './header/unread-messages/unread-messages.service';
import { SendMessageService } from './sendMessage/send-message.service';
import { RecentSurveyService } from './home/disasterList/recent-survey.service';
import { SurveyNotificationsService } from './dashboard/servicesComponets/surveyNotifications/surveyNotifications.service';

/**
 * feature, shared modules
 */
import { ScoreByLocationComponent } from './searchResults/scoreModal/scoreByLocationModal/score-by-location.component';
import { ScoreByDisasterComponent } from './searchResults/scoreModal/scoreByDisasterModal/score-by-disaster.component';
import { LoaderModule } from './common/loader/loader.module';
import { GlobalMapComponent } from './global-map/global-map.component';
import { initializeFabIcons } from 'polyfills';
// import { SidenavService } from './header/sidenav-menu/sidenav-service';
import { LocationMapModule } from './common/locationMap/location-map.module';
// import { SidenavMenuComponent } from './header/sidenav-menu/sidenav-menu.component';
import { SharedServicesFeatureModule } from './sharedServices/shared-services.module';
import { LayoutModule } from '@angular/cdk/layout';
import { IndexablePageLayoutComponent } from './common/indexable-page-layout/indexable-page-layout.component';
import { PageH1Component } from './common/page-h1/page-h1.component';
import { PageH1Service } from './common/page-h1/page-h1.service';

/* router configuration */
const indexableRoutes: Routes = [
  { path: '', component: HomeComponent, data: { pageHeadingKey: 'PAGE_HEADING-HOME' } },
  {
    path: 'login',
    canActivate: [CanActivateViaAlreadyLoggedInGuard],
    loadChildren: () => import('app/login/login.module').then((m) => m.LoginFeatureModule),
    data: { indexable: false },
  },
  {
    path: 'registration',
    loadChildren: () => import('app/registration/registration.module').then((m) => m.RegistrationFeatureModule),
    data: { indexable: false },
  },
  {
    path: 'registration-success',
    loadChildren: () =>
      import('app/registration/registration-success.module').then((m) => m.RegistrationSuccessFeatureModule),
    data: { indexable: false },
  },
  {
    path: 'nominate',
    loadChildren: () => import('app/nominate/nominate.module').then((m) => m.NominateFeatureModule),
    data: { pageHeadingKey: 'NominateComponent-NOMINATE_ORGANIZATION_ACCOUNT_HEADING' },
  },
  {
    path: 'nominate-success',
    loadChildren: () => import('app/nominate/nominate-success.module').then((m) => m.NominateSuccessFeatureModule),
    data: { indexable: false },
  },
  {
    path: 'reset',
    loadChildren: () => import('app/reset/reset.module').then((m) => m.ResetFeatureModule),
    data: { indexable: false },
  },
  {
    path: 'reset-password/:id',
    loadChildren: () => import('app/resetPassword/reset-password.module').then((m) => m.ResetPasswordFeatureModule),
    data: { indexable: false },
  },
  { path: 'volunteers', component: VolunteersPageComponent, data: { pageHeadingKey: 'PAGE_HEADING-VOLUNTEERS' } },
  { path: 'disasters', component: DisastersPageComponent, data: { pageHeadingKey: 'PAGE_HEADING-DISASTERS' } },
  { path: 'privacy-policy', component: PrivacyPolicyPageComponent, data: { pageHeadingKey: 'PAGE_HEADING-PRIVACY' } },
  ABOUT_US_ROUTE,
  NEWS_MEDIA_ROUTE,
  { path: 'donors/services', redirectTo: 'donor-services', pathMatch: 'full' },
  { path: 'donor-services', component: DonorsServicesComponent, data: { pageHeadingKey: 'DONORS-SERVICES_HERO-TITLE' } },
  {
    path: 'what-we-do',
    component: WhatWeDoComponent,
    data: { pageHeadingKey: 'WHAT-WE-DO' },
    children: [
      { path: '', redirectTo: 'smart-response', pathMatch: 'full' },
      { path: 'smart-response', component: SmartResponseComponent },
      { path: 'research', component: ResearchComponent },
      { path: 'advocacy', component: AdvocacyComponent },
    ],
  },
  { path: 'intro', redirectTo: 'why-smartresponse', pathMatch: 'full' },
  { path: 'why-smartresponse', component: IntroComponent, data: { pageHeadingKey: 'HeaderComponent-WHY_SR' } },
  { path: 'global-org-map', redirectTo: 'global-organization-map', pathMatch: 'full' },
  { path: 'global-organization-map', component: GlobalMapComponent, data: { pageHeadingKey: 'Global-mapComponent-TITLE' } },
  { path: 'organization/:id', component: OrgBackgroundComponent },
  { path: 'disaster/:slug', component: DisasterAboutComponent },
  { path: 'disaster/:disasterId/organization/:org_id', component: OrgDisasterComponent },
  { path: 'disaster/:id/:slug', component: DisasterAboutComponent },
  { path: 'location/:id', component: LocationAboutComponent },
  { path: 'org-location/:org_id/:loc_id', component: OrgLocationComponent },
  { path: 'org-disaster/:org_id/:disaster_id', component: OrgDisasterComponent },
  {
    path: 'help/faq/:id',
    loadChildren: () => import('app/help/faq/faq.module').then((m) => m.FrequentlyAskedQuestionsFeatureModule),
    data: { pageHeadingKey: 'PAGE_HEADING-FAQ' },
  },
  // { path: 'disaster-about', component: DisasterAboutComponent, data: { indexable: false } },
  // { path: 'location-about', component: LocationAboutComponent, data: { indexable: false } },
  {
    path: 'dap-donation',
    component: DapDonationComponent,
    data: { pageHeadingKey: 'Dap_Donation-PAGE_TITLE' },
  },
  { path: 'givelively-donation', component: GiveLivelyDonationComponent, data: { indexable: false } },
  {
    path: 'dap-donation-thanks',
    component: DapDonationThanksComponent,
    data: { pageHeadingKey: 'Donation_Thanks-THANK_YOU' },
  },
  {
    path: 'subscribe',
    loadChildren: () => import('app/newsletter/newsletter-subscribe.module').then((m) => m.NewsLetterSubscribeModule),
    data: {
      pageHeadingKey: 'SUBSCRIBE-COMPONENT-TITLE',
    },
  },
  {
    path: 'subscribe-success',
    loadChildren: () =>
      import('app/newsletter/newsletter-subscribe-success.module').then((m) => m.NewsLetterSubscribeSuccessFeatureModule),
    data: { pageHeadingKey: 'SUBSCRIBE-COMPONENT-TITLE' },
  },
  {
    path: 'page-not-found',
    loadChildren: () => import('app/pageNotFound/page-not-found.module').then((m) => m.PageNotFoundFeatureModule),
    data: { pageHeadingKey: 'PAGE_HEADING-NOT_FOUND' },
  },
];

const appRoutes: Routes = [
  {
    path: '',
    component: IndexablePageLayoutComponent,
    children: indexableRoutes,
  },
  {
    path: 'dashboard',
    loadChildren: () => import('app/dashboard/dashboard.module').then((m) => m.DashboardFeatureModule),
    canActivate: [CanActivateViaAuthGuard],
  },
  { path: '**', component: ShortUrlComponent, pathMatch: 'full', data: { indexable: false } },
];

@NgModule({
  imports: [RouterModule.forRoot(appRoutes, { relativeLinkResolution: 'legacy' })],
  exports: [RouterModule],
  declarations: [],
})
export class AppRoutingModule { }

/* translate support */
export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  declarations: [
    AppComponent,
    // SidenavMenuComponent,
    /* our app's components imported in the root module*/
    FooterComponent,
    PrivacyPolicyPageComponent,
    VolunteersPageComponent,
    DisastersPageComponent,
    HeaderComponent,
    PickerMenuComponent,
    OptionPickerComponent,
    CurrencyPickerComponent,
    LanguagePickerComponent,
    MarketingComponent,
    SocialNetworkComponent,
    ShareButtonsComponent,
    ShareStripeComponent,
    DonationModalComponent,
    ResponseProfileComponent,
    OrgLocationComponent,
    OrgDisasterComponent,
    OrgBackgroundComponent,
    DisasterAboutComponent,
    DapDonationComponent,
    GiveLivelyDonationComponent,
    DapDonationThanksComponent,

    ShortUrlComponent,

    LastUpdatedSurveyResultsComponent,
    ParseVideoUrlComponent,

    SurveyResponseComponent,

    SearchComponent,
    DisasterListHomeComponent,
    ReportModalComponent,

    LocationAboutComponent,

    HomeComponent,
    AboutUsComponent,
    MissionComponent,
    HistoryComponent,
    BoardOfDirectorsComponent,
    NewsMediaComponent,
    NewsMediaContentComponent,
    DonorsServicesComponent,
    WhatWeDoComponent,
    SmartResponseComponent,
    ResearchComponent,
    AdvocacyComponent,
    IntroComponent,
    GlobalMapComponent,
    CarouselComponent,

    ScoreByLocationComponent,
    ScoreByDisasterComponent,
    SendMessageComponent,
    UnreadMessagesComponent,
    PageH1Component,
    IndexablePageLayoutComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    GoogleMapsModule,

    ToastrModule.forRoot({
      timeOut: 2000,
      positionClass: 'toast-bottom-right',
      preventDuplicates: true,
    }),

    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    RecaptchaModule,

    AppRoutingModule,

    SharedServicesFeatureModule,
    LocationMapModule,
    MatTooltipModule,

    LoaderModule,

    ShareButtonsModule,
    MatExpansionModule,
    MatSidenavModule,
    FontAwesomeModule,
    MatAutocompleteModule,
    MatInputModule,
    ReactiveFormsModule,
    MatExpansionModule,
    MatIconModule,
    MatTableModule,
    LayoutModule,
    MatListModule,    
    MatToolbarModule 
  ],
  providers: [
    AuthenticationService,
    // SidenavService,
    SendMessageService,
    UnreadMessagesService,
    RecentSurveyService,
    SurveyNotificationsService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeFabIcons,
      deps: [FaIconLibrary],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (pageH1Service: PageH1Service) => () => pageH1Service.initialize(),
      deps: [PageH1Service],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
